import type { ForgeRecipeDef, ForgeRecipeId } from '../core/types';
import { BALANCE } from './balance';

export type { ForgeRecipeDef, ForgeRecipeId };

/**
 * Les six recettes de la **Forge** — révision du GD du 15/10
 * (`docs/INFINI_PROPOSITION.md` §4) et arbitrage **A94** (réponses 7, 8, 9, 15).
 *
 * Prix en Expéditions : `expeditions × Exp(expeditionLevel + usages)` pour une
 * recette qui `escalates` (*Relance*, *Trucage* : 1 Exp(11), puis Exp(12),
 * Exp(13)… sur un même dé), `expeditions × Exp(expeditionLevel)` sinon ;
 * remise de *Marteau Trempé* déduite (`core/forge.ts`). *Artisan des dés*
 * ouvre les trois premières, *Carnet de recettes* les trois autres (A94-8).
 *
 * v0.49.0 (COMMENTS v0.48.1 §1.5, A98) : le *Trempage* coûte
 * `Exp(11 + niveau actuel du dé)` — un dé de niveau 10 fraîchement tiré coûte
 * autant qu'un dé monté dix fois. A98-6 : seuls les dés **Corrupted** se
 * forgent et se sacrifient.
 *
 * v0.51.0 (COMMENTS v0.50.0 §1.1, A101) : les trois recettes de l'Artisan ont
 * un prix de base **en or** (`goldBase` : Relance 650 000, Trempage 200 000,
 * Trucage 1 000 000, contre Exp(11) = 681 510 avant), qui ne suit plus l'or
 * de l'arbre. Les trois recettes du Carnet restent en Expéditions.
 *
 * v0.52.0 (COMMENTS v0.51.0 §1.2, A102) : la *Relance* ne sacrifie **plus
 * aucun dé** ; l'escalade des prix en or est de **+10 % par usage**
 * (`BALANCE.forgeGoldGrowth`, niveau actuel du dé pour le Trempage), et non
 * plus le rapport Exp(N + 1) / Exp(N). Les dés sacrifiés ne changent pas.
 *
 * v0.53.1 (A104) : le *Trempage* croît de **×1,55 par niveau du dé**
 * (`BALANCE.temperGoldGrowth`), Relance et Trucage restant à +10 % par usage.
 */
export const FORGE_RECIPES: ForgeRecipeDef[] = [
  {
    id: 'reroll',
    name: 'Relance',
    description:
      'Relance un affixe du dé : deux affixes neufs sont proposés, ou garder l’actuel. Le ' +
      'premier affixe relancé devient le seul relançable de ce dé ; un affixe d’Absolution ' +
      'ne l’est jamais.',
    requiresNode: 'aby_artisan',
    expeditions: 1,
    expeditionLevel: 11,
    escalates: true,
    goldBase: 650_000,
    consumesText: 'Aucun dé',
  },
  {
    id: 'temper',
    name: 'Trempage',
    description: `Le dé gagne un niveau : dégâts de sa face ×${String(BALANCE.dieLevelFactor).replace('.', ',')}. Le prix suit le niveau actuel du dé.`,
    requiresNode: 'aby_artisan',
    expeditions: 1,
    expeditionLevel: 11,
    escalates: true,
    goldBase: 200_000,
    consumesText: 'Aucun dé',
  },
  {
    id: 'transmute',
    name: 'Trucage',
    description: 'Le dé prend le Matériau des dés sacrifiés.',
    requiresNode: 'aby_artisan',
    expeditions: 1,
    expeditionLevel: 11,
    escalates: true,
    goldBase: 1_000_000,
    consumesText: '10 dés de la rareté du dé et d’un même Matériau',
  },
  {
    id: 'fuse',
    name: 'Fusion',
    description:
      'Fusionne deux dés Rares ou mieux, de même rareté et de même Matériau : le dé obtenu ' +
      'garde un affixe choisi de chacun, les autres sont tirés. Un dé fusionné ne fusionne ' +
      'plus.',
    requiresNode: 'aby_recipes',
    expeditions: 5,
    expeditionLevel: 23,
    escalates: false,
    consumesText: 'Les deux dés fusionnés',
  },
  {
    id: 'sublimate',
    name: 'Sublimation',
    description: '10 % de chance que le dé devienne Enrichi ; les dés sacrifiés sont perdus dans tous les cas.',
    requiresNode: 'aby_recipes',
    expeditions: 5,
    expeditionLevel: 23,
    escalates: false,
    consumesText: '5 dés Enrichis de la rareté du dé',
  },
  {
    id: 'absolve',
    name: 'Absolution',
    description:
      'Ajoute au dé un affixe porté par les dés sacrifiés, valeur tirée au hasard. Un seul ' +
      'affixe d’Absolution par dé, jamais relançable.',
    requiresNode: 'aby_recipes',
    expeditions: 5,
    expeditionLevel: 25,
    escalates: false,
    consumesText: '10 dés Enrichis de la rareté du dé portant l’affixe à ajouter',
  },
];

export const FORGE_RECIPE_BY_ID: Record<ForgeRecipeId, ForgeRecipeDef> = Object.fromEntries(
  FORGE_RECIPES.map((r) => [r.id, r]),
) as Record<ForgeRecipeId, ForgeRecipeDef>;

/**
 * Dés consommés par recette (0 : aucun ; la *Fusion* consomme ses deux dés).
 * La *Relance* n'en consomme plus (3 avant la v0.52.0, A102).
 */
export const FORGE_CONSUMED_COUNT: Record<ForgeRecipeId, number> = {
  reroll: 0,
  temper: 0,
  transmute: 10,
  fuse: 0,
  sublimate: 5,
  absolve: 10,
};
